import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, Loader2, Save, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, humanize } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { Switch } from "@/components/ui/switch";
import { Field, PageHeader, Panel } from "@/components/common";
import SessionsPanel from "@/components/SessionsPanel";

export const ACCOUNT_TABS = [["profile", "My profile", UserRound], ["security", "Account security", ShieldCheck], ["notifications", "Notification settings", Bell]];

function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setName(user?.name || ""), [user?.name]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch("/auth/me", { name }); setUser(data); toast.success("Profile updated"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="My profile" testId="account-profile">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Name"><input data-testid="account-name-input" className="cq-input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Email"><input data-testid="account-email" className="cq-input opacity-70" value={user?.email || ""} disabled /></Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-[#7C8BAD]">
        <span>Sign-in methods: {(user?.auth_providers || []).map(humanize).join(", ")} · Member since {fmtDate(user?.created_at, { dateStyle: "medium" })}</span>
        <button data-testid="account-profile-save" disabled={busy || !name.trim() || name === user?.name} onClick={save} className="cq-btn cq-btn-primary">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
      </div>
    </Panel>
  );
}

function NotificationPrefs() {
  const { path, current } = useWorkspace();
  const q = useWsQuery("/members/me/preferences");
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const on = !!q.data?.briefing_email;
  const toggle = async () => {
    setBusy(true);
    try { await api.put(path("/members/me/preferences"), { briefing_email: !on }); toast.success(on ? "You won't receive the Monday briefing email" : "You'll receive the Monday briefing email"); qc.invalidateQueries({ queryKey: ["ws"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Notification settings" eyebrow={current?.name} testId="settings-notifications">
      <div className="flex items-center justify-between gap-4">
        <div><div className="text-sm text-[#F7FAFF]">Weekly Growth Briefing email</div><p className="text-xs text-[#7C8BAD] mt-0.5">Mondays at 8am (Toronto) for this workspace. Owners and admins are opted in by default. Briefings stay available in the app either way.</p></div>
        <Switch data-testid="settings-briefing-email-toggle" className="data-[state=unchecked]:bg-[#3A4870] data-[state=checked]:bg-[#22E6F2] border border-[#5D6B8C]" checked={on} disabled={busy || q.isLoading} onCheckedChange={toggle} />
      </div>
      <p className="mt-4 text-xs text-[#7C8BAD]">Security emails (new device sign-ins and repeated failed attempts) are always sent and can't be turned off.</p>
    </Panel>
  );
}

export default function Account() {
  const [params, setParams] = useSearchParams();
  const tab = ACCOUNT_TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "profile";
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Account" title="Your account" description="Your profile, sign-in security and notifications. These follow you across workspaces." />
      <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-[#243153]" data-testid="account-tabs">
        {ACCOUNT_TABS.map(([k, label, Icon]) => (
          <button key={k} role="tab" aria-selected={tab === k} data-testid={`account-tab-${k}`} onClick={() => setParams({ tab: k })}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px inline-flex items-center gap-2 transition-colors ${tab === k ? "border-[#22E6F2] text-[#F7FAFF]" : "border-transparent text-[#AAB6CC] hover:text-[#F7FAFF]"}`}>
            <Icon size={14} /> {label}</button>))}
      </div>
      {tab === "profile" && <Profile />}
      {tab === "security" && <SessionsPanel />}
      {tab === "notifications" && <NotificationPrefs />}
    </div>
  );
}
